// EXPERIMENT: constituent_psf_experiment (remove with experiment cleanup)
import { useCallback, useMemo } from 'react';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { useEnrollConstituentPsfExperiment } from '#src/app/pages/psf/shared/hooks';
import { getLocalSignerCounts, getLocalSignerLocation } from '#src/app/pages/psf/shared/utils';
import { useConstituentStatus } from '#src/app/shared/constituents';
import { useIsMobile } from '#src/app/shared/hooks/userAgent';

type Result = Readonly<{
	/** `v2` local signers: the sheet opens on the new local-signer share screen instead of the channel grid. */
	showsLocalSignerShare: boolean;
	/** Enrolls the signer (every arm) when the sheet opens. A no-op for non-participants. */
	enrollOnOpen: () => void;
	screen: Readonly<{ location: string | undefined; isFirstLocalSigner: boolean; otherSignerCount: number }>;
}>;

/**
 * Constituents-in-PSF (CHANGE-100768) on the combo page's "Share petition" sheet. Per the Figma flows,
 * `v2` replaces the sheet's channel grid with the new local-signer share screen; control and `v4` keep
 * the current sheet. Every arm is enrolled when the sheet opens, the earliest point `v2` differs.
 *
 * The variant gate mirrors the share page's: a real (or QA-forced) `v2` assignment, mobile, and an
 * identified constituent. The constituent status is read from sessionStorage, which has long resolved
 * by the time a signer taps the share button; if it has not, the signer gets the current sheet.
 */
export function useLocalSignerShareSlideUp(): Result {
	const {
		data: { petition, constituentPsfExperiment: experiment },
	} = usePsfPromoteOrSharePageData();
	const [constituentStatus] = useConstituentStatus(petition.id);
	const isMobile = useIsMobile();
	const enroll = useEnrollConstituentPsfExperiment({ petitionId: petition.id, petition, experiment });

	const showsLocalSignerShare =
		isMobile &&
		!experiment.fallback &&
		experiment.variation === 'v2_new_share' &&
		Boolean(constituentStatus?.isConstituent);

	const enrollOnOpen = useCallback(() => {
		void enroll(constituentStatus);
	}, [enroll, constituentStatus]);

	const screen = useMemo(
		() => ({ location: getLocalSignerLocation(petition), ...getLocalSignerCounts(petition) }),
		[petition],
	);

	return { showsLocalSignerShare, enrollOnOpen, screen };
}
